import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Mail, Calendar, Cake, Heart, HeartOff, Trash2, CheckCircle, Clock, AlertCircle, Plus, Pencil, Shield, Medal, Award, Star, Trophy, KeyRound, Copy, Check, Crown, Dumbbell } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('usuarios');
  
  // Galones
  const [galones, setGalones] = useState([]);
  const [galonDialogOpen, setGalonDialogOpen] = useState(false);
  const [asignarDialogOpen, setAsignarDialogOpen] = useState(false);
  const [selectedUserForGalon, setSelectedUserForGalon] = useState(null);
  const [userGalones, setUserGalones] = useState([]);
  
  // Reset Password
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    role: 'miembro',
    fecha_nacimiento: '',
    aprobado: true
  });

  const [galonForm, setGalonForm] = useState({
    nombre: '',
    icono: '🏅',
    descripcion: '',
    color: 'gold'
  });

  const [asignarForm, setAsignarForm] = useState({
    galon_id: '',
    mensaje_personalizado: ''
  });

  const iconosDisponibles = ['🏅', '🎖️', '⭐', '🌟', '💪', '🔥', '👑', '🏆', '💎', '🦁', '⚔️', '🛡️', '🎯', '🚀', '💯', '🥇', '🥈', '🥉'];
  const coloresDisponibles = [
    { value: 'gold', label: 'Oro', class: 'bg-yellow-500' },
    { value: 'silver', label: 'Plata', class: 'bg-gray-400' },
    { value: 'bronze', label: 'Bronce', class: 'bg-orange-600' },
    { value: 'blue', label: 'Azul', class: 'bg-blue-500' },
    { value: 'green', label: 'Verde', class: 'bg-green-500' },
    { value: 'red', label: 'Rojo', class: 'bg-red-500' },
    { value: 'purple', label: 'Morado', class: 'bg-purple-500' }
  ];

  useEffect(() => {
    fetchUsuarios();
    fetchGalones();
  }, []);

  const fetchUsuarios = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGalones = async () => {
    try {
      const response = await axios.get(`${API}/galones`);
      setGalones(response.data);
    } catch (error) {
      console.error('Error al cargar galones:', error);
    }
  };

  const fetchUserGalones = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/galones`);
      setUserGalones(response.data);
    } catch (error) {
      console.error('Error al cargar galones del usuario:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      role: 'miembro',
      fecha_nacimiento: '',
      aprobado: true
    });
  };

  const resetGalonForm = () => {
    setGalonForm({
      nombre: '',
      icono: '🏅',
      descripcion: '',
      color: 'gold'
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email || !formData.password) {
      alert('Nombre, email y contraseña son obligatorios');
      return;
    }
    try {
      const response = await axios.post(`${API}/users/admin/create`, formData);
      setUsuarios([...usuarios, response.data]);
      setCreateDialogOpen(false);
      resetForm();
      alert('Usuario creado correctamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        nombre: formData.nombre,
        email: formData.email,
        role: formData.role,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        aprobado: formData.aprobado
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      const response = await axios.put(`${API}/users/admin/${editingUser.id}`, updateData);
      setUsuarios(usuarios.map(u => u.id === editingUser.id ? response.data : u));
      setEditDialogOpen(false);
      setEditingUser(null);
      resetForm();
      alert('Usuario actualizado correctamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const openEditDialog = (usuario) => {
    setEditingUser(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      password: '',
      role: usuario.role,
      fecha_nacimiento: usuario.fecha_nacimiento || '',
      aprobado: usuario.aprobado
    });
    setEditDialogOpen(true);
  };

  const handleDelete = async (userId, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar a "${nombre}"?\n\nEsta acción no se puede deshacer.`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        setUsuarios(usuarios.filter(u => u.id !== userId));
        alert('Usuario eliminado correctamente');
      } catch (error) {
        alert(error.response?.data?.detail || 'Error al eliminar usuario');
      }
    }
  };

  const handleApprove = async (userId, nombre) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`);
      setUsuarios(usuarios.map(u => u.id === userId ? { ...u, aprobado: true } : u));
      alert(`${nombre} ha sido aprobado correctamente`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al aprobar usuario');
    }
  };

  // Función para resetear contraseña
  const handleResetPassword = async (userId) => {
    try {
      const response = await axios.post(`${API}/users/${userId}/reset-password`);
      setResetPasswordResult(response.data);
      setResetPasswordDialogOpen(true);
      setCopiedPassword(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al resetear contraseña');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  // Función para toggle Pro
  const handleTogglePro = async (userId, userName, currentProStatus) => {
    try {
      const response = await axios.put(`${API}/users/${userId}/toggle-pro`);
      setUsuarios(usuarios.map(u => u.id === userId ? { ...u, es_pro: response.data.es_pro } : u));
      alert(`${userName} ${response.data.es_pro ? 'ahora es Cliente Pro' : 'ya no es Cliente Pro'}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cambiar estado Pro');
    }
  };

  // Función para toggle Client Plus
  const handleToggleClientPlus = async (userId, userName, currentRole) => {
    try {
      const response = await axios.put(`${API}/users/${userId}/set-client-plus`);
      setUsuarios(usuarios.map(u => u.id === userId ? { ...u, role: response.data.role } : u));
      alert(`${userName} ${response.data.role === 'client_plus' ? 'ahora es Cliente Plus' : 'vuelve a ser Miembro'}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  // Funciones de Galones
  const handleCreateGalon = async (e) => {
    e.preventDefault();
    if (!galonForm.nombre || !galonForm.descripcion) {
      alert('Nombre y descripción son obligatorios');
      return;
    }
    try {
      const response = await axios.post(`${API}/galones`, galonForm);
      setGalones([...galones, response.data]);
      setGalonDialogOpen(false);
      resetGalonForm();
      alert('Galón creado correctamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear galón');
    }
  };

  const handleDeleteGalon = async (galonId, nombre) => {
    if (window.confirm(`¿Eliminar el galón "${nombre}"?\n\nSe quitará de todos los usuarios que lo tengan.`)) {
      try {
        await axios.delete(`${API}/galones/${galonId}`);
        setGalones(galones.filter(g => g.id !== galonId));
        alert('Galón eliminado');
      } catch (error) {
        alert(error.response?.data?.detail || 'Error al eliminar galón');
      }
    }
  };

  const openAsignarDialog = async (usuario) => {
    setSelectedUserForGalon(usuario);
    await fetchUserGalones(usuario.id);
    setAsignarForm({ galon_id: '', mensaje_personalizado: '' });
    setAsignarDialogOpen(true);
  };

  const handleAsignarGalon = async (e) => {
    e.preventDefault();
    if (!asignarForm.galon_id) {
      alert('Selecciona un galón');
      return;
    }
    try {
      await axios.post(`${API}/galones/asignar`, {
        user_id: selectedUserForGalon.id,
        galon_id: asignarForm.galon_id,
        mensaje_personalizado: asignarForm.mensaje_personalizado || null
      });
      await fetchUserGalones(selectedUserForGalon.id);
      setAsignarForm({ galon_id: '', mensaje_personalizado: '' });
      alert('Galón asignado correctamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al asignar galón');
    }
  };

  const handleQuitarGalon = async (galonId) => {
    try {
      await axios.delete(`${API}/galones/quitar/${selectedUserForGalon.id}/${galonId}`);
      setUserGalones(userGalones.filter(g => g.galon_id !== galonId));
      alert('Galón quitado');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al quitar galón');
    }
  };

  const getColorClass = (color) => {
    const colorMap = {
      gold: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
      silver: 'bg-gray-400/20 border-gray-400 text-gray-300',
      bronze: 'bg-orange-600/20 border-orange-600 text-orange-400',
      blue: 'bg-blue-500/20 border-blue-500 text-blue-400',
      green: 'bg-green-500/20 border-green-500 text-green-400',
      red: 'bg-red-500/20 border-red-500 text-red-400',
      purple: 'bg-purple-500/20 border-purple-500 text-purple-400'
    };
    return colorMap[color] || colorMap.gold;
  };

  const pendingCount = usuarios.filter(u => !u.aprobado && u.role !== 'entrenador').length;
  
  const filteredUsuarios = usuarios.filter(u => {
    if (filter === 'pendientes') return !u.aprobado && u.role !== 'entrenador';
    if (filter === 'aprobados') return u.aprobado || u.role === 'entrenador';
    return true;
  });

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gold mb-2" data-testid="usuarios-title">
            Gestión de Usuarios
          </h1>
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="bg-gray-800">
              <TabsTrigger value="usuarios" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                <User className="w-4 h-4 mr-2" />
                Usuarios ({usuarios.length})
              </TabsTrigger>
              <TabsTrigger value="galones" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                <Medal className="w-4 h-4 mr-2" />
                Galones ({galones.length})
              </TabsTrigger>
            </TabsList>

            {/* Tab Usuarios */}
            <TabsContent value="usuarios" className="mt-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <p className="text-gray-400">Listado completo de miembros del equipo</p>
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary-mk" data-testid="btn-create-user">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-gold">Crear Nuevo Usuario</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="nombre">Nombre *</Label>
                        <Input
                          id="nombre"
                          value={formData.nombre}
                          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Nombre completo"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="email@ejemplo.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="password">Contraseña *</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Mínimo 6 caracteres"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="role">Rol</Label>
                        <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                          <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700 text-white">
                            <SelectItem value="miembro">Miembro</SelectItem>
                            <SelectItem value="entrenador">Entrenador (Admin)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
                        <Input
                          id="fecha_nacimiento"
                          type="date"
                          value={formData.fecha_nacimiento}
                          onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary-mk">
                        Crear Usuario
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Filtros */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button
                  variant={filter === 'todos' ? 'default' : 'outline'}
                  onClick={() => setFilter('todos')}
                  className={filter === 'todos' ? 'btn-primary-mk' : 'border-gray-600 text-gray-300'}
                  size="sm"
                >
                  Todos ({usuarios.length})
                </Button>
                <Button
                  variant={filter === 'pendientes' ? 'default' : 'outline'}
                  onClick={() => setFilter('pendientes')}
                  className={filter === 'pendientes' ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-yellow-600 text-yellow-400'}
                  size="sm"
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Pendientes ({pendingCount})
                </Button>
                <Button
                  variant={filter === 'aprobados' ? 'default' : 'outline'}
                  onClick={() => setFilter('aprobados')}
                  className={filter === 'aprobados' ? 'bg-green-600 hover:bg-green-700' : 'border-green-600 text-green-400'}
                  size="sm"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Aprobados ({usuarios.length - pendingCount})
                </Button>
              </div>

              {/* Alerta de usuarios pendientes */}
              {pendingCount > 0 && filter !== 'aprobados' && (
                <div className="mb-6 bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                  <div>
                    <p className="text-yellow-200 font-medium">
                      {pendingCount} usuario{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''} de aprobación
                    </p>
                    <p className="text-yellow-400/70 text-sm">
                      Los usuarios pendientes no pueden acceder a la aplicación hasta que los apruebes
                    </p>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredUsuarios.map((usuario) => (
                    <Card 
                      key={usuario.id} 
                      className={`card-mk ${!usuario.aprobado && usuario.role !== 'entrenador' ? 'border-yellow-600/50' : ''}`}
                      data-testid={`user-card-${usuario.id}`}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {usuario.foto_perfil ? (
                              <img
                                src={`${BACKEND_URL}${usuario.foto_perfil}`}
                                alt={usuario.nombre}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gold"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
                                <User className="w-6 h-6 text-gray-900" />
                              </div>
                            )}
                            <div>
                              <CardTitle className="text-white mb-1" data-testid="user-name">
                                {usuario.nombre}
                              </CardTitle>
                              <div className="flex gap-1 flex-wrap">
                                <Badge
                                  className={`${
                                    usuario.role === 'entrenador'
                                      ? 'badge-gold'
                                      : 'bg-gray-700 text-gray-300'
                                  }`}
                                  data-testid="user-role"
                                >
                                  {usuario.role === 'entrenador' ? '⚔️ Entrenador' : 'Miembro'}
                                </Badge>
                                {usuario.es_pro && (
                                  <Badge className="bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold">
                                    PRO
                                  </Badge>
                                )}
                                {usuario.role === 'client_plus' && (
                                  <Badge className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold">
                                    PLUS
                                  </Badge>
                                )}
                                {usuario.role !== 'entrenador' && (
                                  <Badge
                                    className={usuario.aprobado 
                                      ? 'bg-green-700 text-green-200' 
                                      : 'bg-yellow-700 text-yellow-200'}
                                  >
                                    {usuario.aprobado ? 'Aprobado' : 'Pendiente'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {/* Botón Pro (solo para miembros) */}
                            {usuario.role !== 'entrenador' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleTogglePro(usuario.id, usuario.nombre, usuario.es_pro)}
                                className={usuario.es_pro ? "text-amber-400 hover:text-amber-300" : "text-gray-400 hover:text-amber-400"}
                                title={usuario.es_pro ? "Quitar Pro" : "Hacer Pro"}
                                data-testid={`btn-toggle-pro-${usuario.id}`}
                              >
                                <Crown className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Botón Client Plus */}
                            {usuario.role !== 'entrenador' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleClientPlus(usuario.id, usuario.nombre, usuario.role)}
                                className={usuario.role === 'client_plus' ? "text-cyan-400 hover:text-cyan-300" : "text-gray-400 hover:text-cyan-400"}
                                title={usuario.role === 'client_plus' ? "Quitar Cliente Plus" : "Hacer Cliente Plus"}
                                data-testid={`btn-toggle-plus-${usuario.id}`}
                              >
                                <Dumbbell className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Botón Reset Password */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPassword(usuario.id)}
                              className="text-blue-400 hover:text-blue-300"
                              title="Resetear contraseña"
                              data-testid={`btn-reset-password-${usuario.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            {/* Botón Galones */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAsignarDialog(usuario)}
                              className="text-yellow-400 hover:text-yellow-300"
                              title="Gestionar galones"
                            >
                              <Medal className="w-4 h-4" />
                            </Button>
                            {/* Botón Editar */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(usuario)}
                              className="text-gray-400 hover:text-gold"
                              data-testid={`btn-edit-${usuario.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm truncate" data-testid="user-email">{usuario.email}</span>
                        </div>
                        
                        {usuario.fecha_nacimiento && (
                          <div className="flex items-center gap-2 text-gray-300">
                            <Cake className="w-4 h-4 flex-shrink-0 text-pink-400" />
                            <span className="text-sm" data-testid="user-cumple">
                              {format(new Date(usuario.fecha_nacimiento + 'T00:00:00'), 'd MMM', { locale: es })}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-gray-300">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm" data-testid="user-fecha">
                            Desde {format(new Date(usuario.fecha_registro), 'd MMM yyyy', { locale: es })}
                          </span>
                        </div>

                        {(usuario.entreno_favorito || usuario.entreno_menos_favorito) && (
                          <div className="pt-2 border-t border-gray-700 space-y-2">
                            {usuario.entreno_favorito && (
                              <div className="flex items-start gap-2">
                                <Heart className="w-3 h-3 text-green-400 mt-1 flex-shrink-0" />
                                <span className="text-xs text-gray-400 line-clamp-2">{usuario.entreno_favorito}</span>
                              </div>
                            )}
                            {usuario.entreno_menos_favorito && (
                              <div className="flex items-start gap-2">
                                <HeartOff className="w-3 h-3 text-red-400 mt-1 flex-shrink-0" />
                                <span className="text-xs text-gray-400 line-clamp-2">{usuario.entreno_menos_favorito}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Botones de acción */}
                        <div className="pt-3 border-t border-gray-700 flex gap-2">
                          {!usuario.aprobado && usuario.role !== 'entrenador' && (
                            <Button
                              onClick={() => handleApprove(usuario.id, usuario.nombre)}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                              data-testid={`btn-approve-${usuario.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobar
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDelete(usuario.id, usuario.nombre)}
                            variant="outline"
                            className="flex-1 border-red-600 text-red-400 hover:bg-red-900/30"
                            size="sm"
                            data-testid={`btn-delete-${usuario.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {filteredUsuarios.length === 0 && (
                    <div className="col-span-full text-center py-20 text-gray-500" data-testid="no-users">
                      {filter === 'pendientes' 
                        ? 'No hay usuarios pendientes de aprobación' 
                        : 'No hay usuarios registrados'}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Tab Galones */}
            <TabsContent value="galones" className="mt-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <p className="text-gray-400">Crea galones y reconocimientos para tu equipo</p>
                <Dialog open={galonDialogOpen} onOpenChange={setGalonDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary-mk">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Galón
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-gold flex items-center gap-2">
                        <Medal className="w-5 h-5" />
                        Crear Nuevo Galón
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateGalon} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-white">Icono</Label>
                        <div className="flex flex-wrap gap-2">
                          {iconosDisponibles.map((icono) => (
                            <button
                              key={icono}
                              type="button"
                              onClick={() => setGalonForm({ ...galonForm, icono })}
                              className={`text-2xl p-2 rounded-lg transition-all ${
                                galonForm.icono === icono 
                                  ? 'bg-gold/30 ring-2 ring-gold' 
                                  : 'bg-gray-800 hover:bg-gray-700'
                              }`}
                            >
                              {icono}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="galon-nombre">Nombre del Galón *</Label>
                        <Input
                          id="galon-nombre"
                          value={galonForm.nombre}
                          onChange={(e) => setGalonForm({ ...galonForm, nombre: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Ej: Guerrero Incansable"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white" htmlFor="galon-descripcion">Descripción *</Label>
                        <Input
                          id="galon-descripcion"
                          value={galonForm.descripcion}
                          onChange={(e) => setGalonForm({ ...galonForm, descripcion: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Ej: Por asistir a 50 entrenamientos"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Color</Label>
                        <div className="flex flex-wrap gap-2">
                          {coloresDisponibles.map((color) => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => setGalonForm({ ...galonForm, color: color.value })}
                              className={`px-3 py-1 rounded-full text-sm transition-all ${color.class} ${
                                galonForm.color === color.value 
                                  ? 'ring-2 ring-white' 
                                  : 'opacity-60 hover:opacity-100'
                              }`}
                            >
                              {color.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Preview */}
                      <div className="p-4 bg-gray-800 rounded-lg">
                        <p className="text-gray-400 text-xs mb-2">Vista previa:</p>
                        <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getColorClass(galonForm.color)}`}>
                          <span className="text-xl">{galonForm.icono}</span>
                          <span className="font-medium">{galonForm.nombre || 'Nombre del galón'}</span>
                        </div>
                      </div>
                      <Button type="submit" className="w-full btn-primary-mk">
                        Crear Galón
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {galones.length === 0 ? (
                <div className="text-center py-20 text-gray-500">
                  <Medal className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No hay galones creados</p>
                  <p className="text-sm">Crea el primer galón para reconocer a tu equipo</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {galones.map((galon) => (
                    <Card key={galon.id} className="card-mk">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getColorClass(galon.color)}`}>
                            <span className="text-2xl">{galon.icono}</span>
                            <div>
                              <p className="font-medium">{galon.nombre}</p>
                              <p className="text-xs opacity-70">{galon.descripcion}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteGalon(galon.id, galon.nombre)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialog Editar Usuario */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                Editar Usuario
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white" htmlFor="edit-nombre">Nombre</Label>
                <Input
                  id="edit-nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="edit-password">Nueva Contraseña (dejar vacío para no cambiar)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Nueva contraseña..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="edit-role">Rol</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="miembro">Miembro</SelectItem>
                    <SelectItem value="entrenador">⚔️ Entrenador (Admin)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.role === 'entrenador' && (
                  <p className="text-xs text-yellow-400 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    Los entrenadores tienen acceso completo a la administración
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="edit-fecha_nacimiento">Fecha de Nacimiento</Label>
                <Input
                  id="edit-fecha_nacimiento"
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-aprobado"
                  checked={formData.aprobado}
                  onChange={(e) => setFormData({ ...formData, aprobado: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label className="text-white" htmlFor="edit-aprobado">Usuario Aprobado</Label>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1 border-gray-600" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 btn-primary-mk">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Asignar Galones */}
        <Dialog open={asignarDialogOpen} onOpenChange={setAsignarDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Medal className="w-5 h-5" />
                Galones de {selectedUserForGalon?.nombre}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Galones actuales del usuario */}
              {userGalones.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Galones actuales:</p>
                  <div className="flex flex-wrap gap-2">
                    {userGalones.map((g) => (
                      <div 
                        key={g.id} 
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border ${getColorClass(g.galon_color)} group`}
                      >
                        <span>{g.galon_icono}</span>
                        <span className="text-sm">{g.galon_nombre}</span>
                        <button
                          onClick={() => handleQuitarGalon(g.galon_id)}
                          className="ml-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Asignar nuevo galón */}
              <form onSubmit={handleAsignarGalon} className="space-y-3 pt-3 border-t border-gray-700">
                <p className="text-sm text-gray-400">Asignar nuevo galón:</p>
                {galones.length === 0 ? (
                  <p className="text-yellow-400 text-sm">No hay galones creados. Crea uno en la pestaña "Galones".</p>
                ) : (
                  <>
                    <Select value={asignarForm.galon_id} onValueChange={(value) => setAsignarForm({ ...asignarForm, galon_id: value })}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Selecciona un galón..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        {galones.filter(g => !userGalones.find(ug => ug.galon_id === g.id)).map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.icono} {g.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Mensaje personalizado (opcional)"
                      value={asignarForm.mensaje_personalizado}
                      onChange={(e) => setAsignarForm({ ...asignarForm, mensaje_personalizado: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Button type="submit" className="w-full btn-primary-mk" disabled={!asignarForm.galon_id}>
                      <Award className="w-4 h-4 mr-2" />
                      Otorgar Galón
                    </Button>
                  </>
                )}
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Contraseña Reseteada */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Contraseña Reseteada
              </DialogTitle>
            </DialogHeader>
            {resetPasswordResult && (
              <div className="space-y-4">
                <p className="text-gray-300">
                  Se ha generado una nueva contraseña temporal para <strong className="text-white">{resetPasswordResult.user_name}</strong>
                </p>
                
                <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                  <div>
                    <Label className="text-gray-400 text-sm">Email</Label>
                    <p className="text-white font-mono">{resetPasswordResult.user_email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Nueva contraseña temporal</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-700 px-3 py-2 rounded text-gold font-mono text-lg flex-1">
                        {resetPasswordResult.temp_password}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(resetPasswordResult.temp_password)}
                        className={copiedPassword ? 'border-green-500 text-green-500' : 'border-gray-600'}
                      >
                        {copiedPassword ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-900/30 border border-yellow-600/50 p-3 rounded-lg">
                  <p className="text-yellow-200 text-sm">
                    ⚠️ Comunica esta contraseña al usuario de forma segura. El usuario debería cambiarla después de iniciar sesión.
                  </p>
                </div>
                
                <Button 
                  className="w-full btn-primary-mk"
                  onClick={() => setResetPasswordDialogOpen(false)}
                >
                  Entendido
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default Usuarios;
